import React from "react";
import { motion } from "framer-motion";
import { Star, MapPin, Wifi, Coffee, ArrowUpRight, Bookmark } from "lucide-react";
import { money2 } from "@/lib/api";

export const Stars = ({ n, size = "h-3.5 w-3.5" }) => (
  <span className="inline-flex items-center gap-0.5 text-amber-400" aria-label={`${n} star`}>
    {Array.from({ length: Math.round(n) }).map((_, i) => <Star key={i} className={`${size} fill-current`} />)}
  </span>
);

export const scoreLabel = (s) => (s >= 9 ? "Exceptional" : s >= 8.5 ? "Excellent" : s >= 8 ? "Very good" : s >= 7 ? "Good" : s > 0 ? "Fair" : "");

export function HotelCard({ hotel, index, nights, destination, onOpen, onSave }) {
  const perkIcon = { "Free Wi-Fi": Wifi, "Breakfast included": Coffee };
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: Math.min(index, 8) * 0.06 }}
      onClick={() => onOpen(hotel)}
      className="group cursor-pointer overflow-hidden rounded-3xl bg-card shadow-[0_2px_12px_-4px_rgba(0,0,0,0.25)] ring-1 ring-border/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-16px_rgba(0,0,0,0.5)] hover:ring-accent/40"
      data-testid={`hotel-card-${hotel.hotel_id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
        {hotel.image ? (
          <img src={hotel.image} alt={hotel.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground"><MapPin className="h-8 w-8" /></div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent" />
        {hotel.review_score > 0 && (
          <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-md" data-testid={`hotel-score-${hotel.hotel_id}`}>
            <span className="rounded-md bg-accent px-1.5 py-0.5 text-[11px] text-accent-foreground">{hotel.review_score.toFixed(1)}</span> {scoreLabel(hotel.review_score)}
          </div>
        )}
        <div className="absolute right-3 top-3 flex items-center gap-2">
          {hotel.discount_percentage > 0 && <span className="rounded-full bg-rose-500/90 px-2.5 py-1 text-[11px] font-semibold text-white">-{hotel.discount_percentage}%</span>}
          <button type="button" onClick={(e) => { e.stopPropagation(); onSave(hotel); }} className="flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-md transition-all hover:bg-accent hover:text-accent-foreground" aria-label="Save to trip" data-testid={`hotel-save-${hotel.hotel_id}`}><Bookmark className="h-4 w-4" /></button>
        </div>
        {hotel.star_rating > 0 && <div className="absolute bottom-3 left-3"><Stars n={hotel.star_rating} /></div>}
      </div>
      <div className="p-4">
        <h3 className="line-clamp-1 font-display text-base font-semibold" data-testid={`hotel-name-${hotel.hotel_id}`}>{hotel.name}</h3>
        <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {hotel.address || hotel.city || destination}</p>
        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
          {hotel.description || `${hotel.star_rating ? `${hotel.star_rating}-star ` : ""}stay in ${destination}${hotel.review_count ? ` loved by ${hotel.review_count.toLocaleString()} guests` : ""}.`}
        </p>
        <div className="mt-3 flex items-center gap-2">
          {hotel.perks.map((p) => { const I = perkIcon[p] || Wifi; return <span key={p} className="inline-flex items-center gap-1 rounded-full bg-secondary/70 px-2 py-0.5 text-[11px] text-muted-foreground"><I className="h-3 w-3" /> {p}</span>; })}
        </div>
        <div className="mt-4 flex items-end justify-between">
          <div>
            {hotel.crossed_out_rate > hotel.price_per_night && <p className="text-xs text-muted-foreground line-through">{money2(hotel.crossed_out_rate)}</p>}
            <p className="font-display text-xl font-bold" data-testid={`hotel-price-${hotel.hotel_id}`}>{money2(hotel.price_per_night)}<span className="ml-1 text-xs font-normal text-muted-foreground">/ night</span></p>
            {nights > 1 && <p className="text-[11px] text-muted-foreground">{money2(hotel.total_price)} for {nights} nights</p>}
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-3 py-1.5 text-xs font-semibold text-accent transition-colors group-hover:bg-accent group-hover:text-accent-foreground">View <ArrowUpRight className="h-3.5 w-3.5" /></span>
        </div>
      </div>
    </motion.article>
  );
}
